/**
 * Selected-neighborhood rent utilities.
 *
 * A "selected neighborhood" persists the user's choice from the Locations page
 * and provides a monthly rent estimate that flows into Budget and Forecast.
 */

import type { ExpandedNeighborhood } from "./neighborhoodData";

export interface SelectedNeighborhood {
  cityKey: string;        // lowercased city name (matches EXPANDED_NEIGHBORHOODS key)
  name: string;           // neighborhood display name
  rentMin: number;        // raw min from data
  rentMax: number;        // raw max from data
  rentDisplay: string;    // original display string (with units)
  /** Monthly rent estimate in local currency, computed at selection time. */
  monthlyRentLocal: number;
}

const SQFT_PER_M2 = 10.7639;
const SQFT_PER_TSUBO = 35.583;

/**
 * Convert a neighborhood's rent range into a monthly rent figure in local
 * currency, using the unit suffix in `rentDisplay` and a target sqft.
 *
 * Supported units (parsed from `rentDisplay`):
 *  - "/sqft/yr"   -> midpoint × sqft / 12
 *  - "/m²/yr"     -> midpoint × m²   / 12
 *  - "/m²/mo"     -> midpoint × m²
 *  - "/tsubo/mo"  -> midpoint × tsubo
 *
 * `targetSqft` defaults to 1200 sqft (~111 m²), a typical small F&B footprint.
 */
export function computeMonthlyRentForNeighborhood(
  n: Pick<ExpandedNeighborhood, "rentMin" | "rentMax" | "rentDisplay">,
  targetSqft = 1200,
): number {
  const mid = (n.rentMin + n.rentMax) / 2;
  const display = n.rentDisplay.toLowerCase();
  const m2 = targetSqft / SQFT_PER_M2;
  const tsubo = targetSqft / SQFT_PER_TSUBO;

  let monthly: number;
  if (display.includes("/sqft/yr") || display.includes("/sq ft/yr")) {
    monthly = (mid * targetSqft) / 12;
  } else if (display.includes("/m²/yr") || display.includes("/m2/yr")) {
    monthly = (mid * m2) / 12;
  } else if (display.includes("/m²/mo") || display.includes("/m2/mo")) {
    monthly = mid * m2;
  } else if (display.includes("/tsubo/mo")) {
    monthly = mid * tsubo;
  } else {
    // Fallback: assume sqft/yr
    monthly = (mid * targetSqft) / 12;
  }
  return Math.round(monthly);
}

/** Build a SelectedNeighborhood from a neighborhood + the active city key. */
export function buildSelectedNeighborhood(
  cityKey: string,
  n: ExpandedNeighborhood,
  targetSqft = 1200,
): SelectedNeighborhood {
  return {
    cityKey,
    name: n.name,
    rentMin: n.rentMin,
    rentMax: n.rentMax,
    rentDisplay: n.rentDisplay,
    monthlyRentLocal: computeMonthlyRentForNeighborhood(n, targetSqft),
  };
}

/**
 * Compute the annual rent (in local currency) implied by a selected
 * neighborhood — i.e., midpoint of its rent range × the venue size.
 * This is the canonical figure that drives the "Deposits & Rent" line
 * of the startup budget when a neighborhood is selected.
 */
export function computeAnnualRentForNeighborhood(
  n: Pick<ExpandedNeighborhood, "rentMin" | "rentMax" | "rentDisplay">,
  targetSqft = 1200,
): number {
  return computeMonthlyRentForNeighborhood(n, targetSqft) * 12;
}

/**
 * Replace the rent/deposits portion of a previously-computed startup total
 * with one driven by the selected neighborhood's midpoint $/sqft/yr × venue
 * size. Returns the adjusted total in the same currency as `baseTotal`.
 *
 * Formula:
 *   adjustedTotal = baseTotal - oldDepositsLine + newDepositsLine
 *   newDepositsLine = annualRent (12 months operating rent)
 *                   + 2 months rent (security deposit)
 *                   + 1 month rent (first month) — already in annual
 *   ≈ annualRent + 2 monthsRent  (deposit only; first/last embedded in 12mo)
 */
export function applyNeighborhoodToStartupTotal(opts: {
  baseTotal: number;
  oldDepositsLine: number;
  selected: SelectedNeighborhood | null;
}): number {
  const { baseTotal, oldDepositsLine, selected } = opts;
  if (!selected) return baseTotal;
  const annualRent = selected.monthlyRentLocal * 12;
  const securityDeposit = selected.monthlyRentLocal * 2;
  const newDepositsLine = annualRent + securityDeposit;
  const adjusted = baseTotal - oldDepositsLine + newDepositsLine;
  return Math.max(Math.round(adjusted), 0);
}
